import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  ArrowLeft, Calculator, Database, TrendingUp, Loader2, Save, RefreshCw,
  AlertCircle, CheckCircle2, ChevronRight, Lock, Building2, History, Trash2,
  Layers,
} from 'lucide-react';
import { toast } from 'sonner';

const apiBase = () => (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const fmtCHF = (n) =>
  new Intl.NumberFormat('de-CH', { maximumFractionDigits: 2 }).format(Number(n) || 0);
const fmtInt = (n) =>
  new Intl.NumberFormat('de-CH').format(Math.round(Number(n) || 0));

const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 8 }, (_, i) => currentYear - i);

export const AdminValuation = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [abaSettings, setAbaSettings] = useState({ accountUuid: '', apiToken: '', hasToken: false, tokenPreview: '' });
  const [savingSettings, setSavingSettings] = useState(false);
  const [syncRange, setSyncRange] = useState({ yearFrom: currentYear - 4, yearTo: currentYear - 1 });
  const [syncing, setSyncing] = useState(false);
  const [syncData, setSyncData] = useState(null);
  const [snapshotId, setSnapshotId] = useState(null);
  const [expandedYear, setExpandedYear] = useState(null);
  const [savingAdjustment, setSavingAdjustment] = useState(false);
  const [newManual, setNewManual] = useState({ label: '', kind: 'revenue', amount: 0 });
  const [snapshotsList, setSnapshotsList] = useState([]);

  const [financeAdj, setFinanceAdj] = useState({
    avgRevenue: 0, avgExpenses: 0,
    ebitMarginAdj: 0, taxRate: 14.93, capex: 0,
  });

  const [dcfParams, setDcfParams] = useState({
    growth1: 8, growth2: 4, wacc: 10, terminalGrowth: 2,
    netDebt: 0, shares: 10000, marginOfSafety: 25, note: '',
  });
  const [calculating, setCalculating] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/admin/login');
      return;
    }
    loadAbaSettings();
    loadHistory();
    loadSnapshots();
  }, [user, navigate]);

  const loadAbaSettings = async () => {
    try {
      const r = await fetch(apiBase() + '/api/admin/valuation/abaninja/settings', { credentials: 'include' });
      const d = await r.json();
      if (r.ok && d.success && d.settings) {
        setAbaSettings({ ...abaSettings, ...d.settings, apiToken: '' });
        if (d.settings.accountUuid && d.settings.hasToken) setStep(2);
      }
    } catch { /* ignore */ }
  };

  const loadHistory = async () => {
    try {
      const r = await fetch(apiBase() + '/api/admin/valuation/dcf/history', { credentials: 'include' });
      const d = await r.json();
      if (r.ok && d.success) setHistory(d.valuations || []);
    } catch { /* ignore */ }
  };

  const loadSnapshots = async () => {
    try {
      const r = await fetch(apiBase() + '/api/admin/valuation/snapshots', { credentials: 'include' });
      const d = await r.json();
      if (r.ok && d.success) setSnapshotsList(d.snapshots || []);
    } catch { /* ignore */ }
  };

  const loadSnapshot = async (id) => {
    setSyncing(true);
    try {
      const r = await fetch(apiBase() + `/api/admin/valuation/snapshots/${id}`, { credentials: 'include' });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || 'Snapshot konnte nicht geladen werden');
      setSyncData(d.snapshot.data);
      setSnapshotId(d.snapshot.id);
      setSyncRange({ yearFrom: d.snapshot.yearFrom, yearTo: d.snapshot.yearTo });
      toast.success('Snapshot geladen');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSyncing(false);
    }
  };

  const deleteSnapshot = async (id) => {
    if (!window.confirm('Snapshot wirklich löschen?')) return;
    try {
      const r = await fetch(apiBase() + `/api/admin/valuation/snapshots/${id}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (!r.ok) throw new Error('Löschen fehlgeschlagen');
      toast.success('Snapshot gelöscht');
      loadSnapshots();
      if (snapshotId === id) { setSyncData(null); setSnapshotId(null); }
    } catch (e) {
      toast.error(e.message);
    }
  };

  const saveAbaSettings = async () => {
    if (!abaSettings.accountUuid.trim()) {
      toast.error('Account UUID erforderlich');
      return;
    }
    setSavingSettings(true);
    try {
      const r = await fetch(apiBase() + '/api/admin/valuation/abaninja/settings', {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          accountUuid: abaSettings.accountUuid.trim(),
          apiToken: abaSettings.apiToken.trim() || null,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || 'Speichern fehlgeschlagen');
      toast.success('AbaNinja-Einstellungen gespeichert');
      setAbaSettings({ ...abaSettings, ...d.settings, apiToken: '' });
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSavingSettings(false);
    }
  };

  const runSync = async () => {
    setSyncing(true);
    setSyncData(null);
    try {
      const r = await fetch(apiBase() + '/api/admin/valuation/abaninja/sync', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(syncRange),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || 'Sync fehlgeschlagen');
      setSyncData(d);
      setSnapshotId(d.snapshotId || null);
      // Only overwrite user-entered values if sync returned non-zero averages
      const avg = d.averages || {};
      if ((avg.revenue || 0) > 0 || (avg.expenses || 0) > 0) {
        setFinanceAdj({
          ...financeAdj,
          avgRevenue: avg.revenue || financeAdj.avgRevenue,
          avgExpenses: avg.expenses || financeAdj.avgExpenses,
        });
      }
      toast.success(`${d.years.length} Jahre geladen`);
      // remove auto-jump so user can review documents first
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSyncing(false);
    }
  };

  const toggleDocExclusion = async (year, docId, currentlyExcluded) => {
    if (!snapshotId) return;
    setSavingAdjustment(true);
    try {
      const r = await fetch(apiBase() + `/api/admin/valuation/snapshots/${snapshotId}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ year, docAdjustments: [{ docId, excluded: !currentlyExcluded }] }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || 'Anpassung fehlgeschlagen');
      setSyncData((prev) => prev && {
        ...prev,
        perYear: prev.perYear.map((y) => y.year === year ? d.yearEntry : y),
        averages: d.averages,
      });
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSavingAdjustment(false);
    }
  };

  const setManualAdjustments = async (year, list) => {
    if (!snapshotId) return;
    setSavingAdjustment(true);
    try {
      const r = await fetch(apiBase() + `/api/admin/valuation/snapshots/${snapshotId}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ year, manualAdjustments: list }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || 'Anpassung fehlgeschlagen');
      setSyncData((prev) => prev && {
        ...prev,
        perYear: prev.perYear.map((y) => y.year === year ? d.yearEntry : y),
        averages: d.averages,
      });
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSavingAdjustment(false);
    }
  };

  const addManualAdjustment = (year) => {
    if (!newManual.label.trim() || !newManual.amount) {
      toast.error('Bitte Bezeichnung und Betrag eingeben');
      return;
    }
    const yearEntry = (syncData?.perYear || []).find((y) => y.year === year);
    const list = [...(yearEntry?.manualAdjustments || []), {
      label: newManual.label.trim(),
      kind: newManual.kind,
      amount: parseFloat(newManual.amount) || 0,
    }];
    setManualAdjustments(year, list);
    setNewManual({ label: '', kind: 'revenue', amount: 0 });
  };

  const removeManualAdjustment = (year, index) => {
    const yearEntry = (syncData?.perYear || []).find((y) => y.year === year);
    const list = (yearEntry?.manualAdjustments || []).filter((_, i) => i !== index);
    setManualAdjustments(year, list);
  };

  const applyAverages = () => {
    if (!syncData?.averages) return;
    setFinanceAdj({
      ...financeAdj,
      avgRevenue: syncData.averages.revenue || 0,
      avgExpenses: syncData.averages.expenses || 0,
    });
    toast.success('Mittelwerte übernommen');
  };

  // Live FCF preview
  const computedFcf = useMemo(() => {
    const rev = Number(financeAdj.avgRevenue) || 0;
    const exp = Number(financeAdj.avgExpenses) || 0;
    const adj = Number(financeAdj.ebitMarginAdj) || 0;
    const tax = Number(financeAdj.taxRate) || 0;
    const capex = Number(financeAdj.capex) || 0;
    const ebit = (rev - exp) * (1 + adj / 100);
    const nopat = ebit * (1 - tax / 100);
    const fcf = nopat - capex;
    return { ebit, nopat, fcf };
  }, [financeAdj]);

  const calculate = async () => {
    setCalculating(true);
    try {
      const r = await fetch(apiBase() + '/api/admin/valuation/dcf/calculate', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fcfBase: computedFcf.fcf,
          growth1: Number(dcfParams.growth1),
          growth2: Number(dcfParams.growth2),
          wacc: Number(dcfParams.wacc),
          terminalGrowth: Number(dcfParams.terminalGrowth),
          netDebt: Number(dcfParams.netDebt),
          shares: parseInt(dcfParams.shares, 10),
          marginOfSafety: Number(dcfParams.marginOfSafety),
          note: dcfParams.note,
          avgRevenue: Number(financeAdj.avgRevenue) || null,
          avgExpenses: Number(financeAdj.avgExpenses) || null,
          ebitMarginAdj: Number(financeAdj.ebitMarginAdj) || 0,
          taxRate: Number(financeAdj.taxRate) || 0,
          capex: Number(financeAdj.capex) || 0,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || 'Berechnung fehlgeschlagen');
      setResult(d.valuation);
      toast.success('DCF berechnet & gespeichert');
      loadHistory();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setCalculating(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="border-b border-[#C4A55A]/20 bg-black/80 backdrop-blur-md sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex items-center gap-3">
          <Link to="/admin/dashboard" className="text-gray-400 hover:text-[#C4A55A]" data-testid="back-to-admin">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <Calculator className="h-6 w-6 text-[#C4A55A]" />
          <h1 className="text-xl font-semibold">DCF-Unternehmensbewertung</h1>
          <span className="text-xs text-amber-300 bg-amber-500/15 border border-amber-500/30 px-2 py-0.5 rounded ml-2">
            Nur intern – nicht öffentlich
          </span>
          <Link to="/admin/valuation/combined"
                data-testid="goto-combined"
                className="ml-auto inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gradient-to-r from-[#C4A55A] to-[#B8974E] text-black text-sm font-semibold">
            <Layers className="h-4 w-4" /> Kombinierte Bewertung →
          </Link>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-5xl space-y-6">
        {/* Step indicator */}
        <div className="flex items-center gap-3 text-sm">
          {[1, 2, 3].map((s) => (
            <React.Fragment key={s}>
              <button
                onClick={() => setStep(s)}
                data-testid={`step-${s}`}
                className={`px-3 py-1.5 rounded-lg border ${
                  step === s ? 'bg-[#C4A55A] text-black border-[#C4A55A] font-semibold' :
                  step > s ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30' :
                  'bg-[#1a1a1a] text-gray-500 border-white/10'
                }`}
              >
                {s === 1 ? '1. AbaNinja' : s === 2 ? '2. Finanzdaten' : '3. DCF'}
              </button>
              {s < 3 && <ChevronRight className="h-4 w-4 text-gray-600" />}
            </React.Fragment>
          ))}
        </div>

        {/* Step 1: AbaNinja Settings */}
        {step === 1 && (
          <section className="bg-[#0f0f0f] border border-white/10 rounded-2xl p-6" data-testid="step1-content">
            <div className="flex items-center gap-2 mb-4">
              <Database className="h-5 w-5 text-[#C4A55A]" />
              <h2 className="text-lg font-semibold">AbaNinja-Verbindung</h2>
            </div>
            <p className="text-sm text-gray-400 mb-5">
              Geben Sie die Account-UUID und den API-Token Ihres AbaNinja-Kontos ein.
              Die Daten werden verschlüsselt gespeichert und nur serverseitig verwendet.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
              <label className="block">
                <span className="text-xs text-gray-400">Account UUID</span>
                <input
                  type="text"
                  value={abaSettings.accountUuid}
                  onChange={(e) => setAbaSettings({ ...abaSettings, accountUuid: e.target.value })}
                  className="mt-1 w-full bg-black/60 border border-white/15 rounded-lg px-3 py-2 text-white font-mono text-sm focus:border-[#C4A55A] focus:outline-none"
                  data-testid="aba-uuid"
                  placeholder="00000000-0000-0000-0000-000000000000"
                />
              </label>
              <label className="block">
                <span className="text-xs text-gray-400 flex items-center gap-2">
                  API-Token <Lock className="h-3 w-3" />
                  {abaSettings.hasToken && (
                    <span className="text-emerald-400">gespeichert ({abaSettings.tokenPreview})</span>
                  )}
                </span>
                <input
                  type="password"
                  value={abaSettings.apiToken}
                  onChange={(e) => setAbaSettings({ ...abaSettings, apiToken: e.target.value })}
                  className="mt-1 w-full bg-black/60 border border-white/15 rounded-lg px-3 py-2 text-white font-mono text-sm focus:border-[#C4A55A] focus:outline-none"
                  data-testid="aba-token"
                  placeholder={abaSettings.hasToken ? 'leer lassen = bisheriger Token behalten' : 'eyJ…'}
                />
              </label>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={saveAbaSettings}
                disabled={savingSettings}
                data-testid="aba-save"
                className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-gradient-to-r from-[#C4A55A] to-[#B8974E] text-black font-semibold disabled:opacity-50"
              >
                {savingSettings ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Speichern
              </button>
              <button
                onClick={() => setStep(2)}
                disabled={!abaSettings.hasToken && !abaSettings.apiToken}
                data-testid="aba-next"
                className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-[#1a1a1a] border border-[#C4A55A]/40 text-[#C4A55A] hover:bg-[#C4A55A]/10 disabled:opacity-40"
              >
                Weiter <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </section>
        )}

        {/* Step 2: Sync + adjust */}
        {step === 2 && (
          <section className="bg-[#0f0f0f] border border-white/10 rounded-2xl p-6" data-testid="step2-content">
            <div className="flex items-center gap-2 mb-4">
              <RefreshCw className="h-5 w-5 text-[#C4A55A]" />
              <h2 className="text-lg font-semibold">Buchhaltungsdaten laden & anpassen</h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
              <label className="block">
                <span className="text-xs text-gray-400">Von Jahr</span>
                <select
                  value={syncRange.yearFrom}
                  onChange={(e) => setSyncRange({ ...syncRange, yearFrom: parseInt(e.target.value, 10) })}
                  className="mt-1 w-full bg-black/60 border border-white/15 rounded-lg px-3 py-2 text-white"
                  data-testid="sync-year-from"
                >
                  {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-xs text-gray-400">Bis Jahr</span>
                <select
                  value={syncRange.yearTo}
                  onChange={(e) => setSyncRange({ ...syncRange, yearTo: parseInt(e.target.value, 10) })}
                  className="mt-1 w-full bg-black/60 border border-white/15 rounded-lg px-3 py-2 text-white"
                  data-testid="sync-year-to"
                >
                  {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </label>
              <button
                onClick={runSync}
                disabled={syncing}
                data-testid="sync-run"
                className="self-end inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-semibold disabled:opacity-50"
              >
                {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                Aus AbaNinja laden
              </button>
            </div>

            {snapshotsList.length > 0 && (
              <div className="bg-black/40 border border-white/5 rounded-xl p-3 mb-5" data-testid="snapshot-history">
                <div className="text-xs uppercase tracking-wider text-gray-400 mb-2">
                  Bisherige Snapshots ({snapshotsList.length}) – ohne erneuten API-Call laden
                </div>
                <div className="space-y-1">
                  {snapshotsList.slice(0, 6).map((s) => (
                    <div key={s.id}
                         data-testid={`snapshot-row-${s.id}`}
                         className={`flex items-center justify-between text-sm px-2 py-1.5 rounded hover:bg-white/5 ${snapshotId === s.id ? 'bg-[#C4A55A]/10 border border-[#C4A55A]/30' : ''}`}>
                      <span className="text-gray-300">
                        {new Date(s.fetchedAt).toLocaleString('de-CH')}
                        <span className="text-gray-500 ml-2">({s.yearFrom}–{s.yearTo})</span>
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 tabular-nums hidden sm:inline">
                          Ø EBIT CHF {fmtCHF(s.averages?.ebit || 0)}
                        </span>
                        <button onClick={() => loadSnapshot(s.id)}
                                data-testid={`snapshot-load-${s.id}`}
                                className="text-xs px-2 py-0.5 rounded bg-[#C4A55A]/15 text-[#C4A55A] hover:bg-[#C4A55A]/25">
                          Laden
                        </button>
                        <button onClick={() => deleteSnapshot(s.id)}
                                data-testid={`snapshot-delete-${s.id}`}
                                className="text-xs text-gray-500 hover:text-red-400">×</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {syncData && (
              <div className="bg-black/40 border border-white/5 rounded-xl p-4 mb-5" data-testid="sync-results">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs uppercase tracking-wider text-gray-400">Jahresübersicht aus AbaNinja</div>
                  {snapshotId && (
                    <button
                      onClick={applyAverages}
                      data-testid="apply-averages"
                      disabled={savingAdjustment}
                      className="text-xs px-3 py-1 rounded bg-[#C4A55A]/15 border border-[#C4A55A]/40 text-[#C4A55A] hover:bg-[#C4A55A]/25 disabled:opacity-50"
                    >
                      Ø Mittelwerte übernehmen
                    </button>
                  )}
                </div>
                <table className="w-full text-sm">
                  <thead className="text-xs text-gray-500">
                    <tr><th className="text-left py-2"></th>
                        <th className="text-left py-2">Jahr</th>
                        <th className="text-right py-2">Umsatz</th>
                        <th className="text-right py-2">Aufwand</th>
                        <th className="text-right py-2">EBIT</th>
                        <th className="text-right py-2">Marge</th>
                        <th className="text-right py-2">Belege (R/A)</th></tr>
                  </thead>
                  <tbody>
                    {syncData.perYear.map((p) => (
                      <React.Fragment key={p.year}>
                        <tr className="border-t border-white/5">
                          <td className="py-2">
                            <button
                              onClick={() => setExpandedYear(expandedYear === p.year ? null : p.year)}
                              data-testid={`expand-year-${p.year}`}
                              className="text-[#C4A55A] hover:text-[#D4B56A] text-sm"
                            >
                              {expandedYear === p.year ? '▼' : '▶'}
                            </button>
                          </td>
                          <td className="py-2 font-medium">{p.year}</td>
                          <td className="py-2 text-right tabular-nums">{fmtCHF(p.revenue)}</td>
                          <td className="py-2 text-right tabular-nums">{fmtCHF(p.expenses)}</td>
                          <td className="py-2 text-right tabular-nums">{fmtCHF(p.ebit)}</td>
                          <td className="py-2 text-right tabular-nums">{p.ebitMargin}%</td>
                          <td className="py-2 text-right text-gray-500">{p.invoiceCount}/{p.receiptCount}</td>
                        </tr>
                        {expandedYear === p.year && (
                          <tr>
                            <td colSpan={7} className="bg-black/60 p-3" data-testid={`year-details-${p.year}`}>
                              <DocumentDetail
                                year={p}
                                onToggleExclude={(docId, ex) => toggleDocExclusion(p.year, docId, ex)}
                                onAddManual={() => addManualAdjustment(p.year)}
                                onRemoveManual={(idx) => removeManualAdjustment(p.year, idx)}
                                newManual={newManual}
                                setNewManual={setNewManual}
                                saving={savingAdjustment}
                              />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                    <tr className="border-t-2 border-[#C4A55A]/40 text-[#C4A55A] font-semibold">
                      <td></td>
                      <td className="py-2">Ø Durchschnitt</td>
                      <td className="py-2 text-right tabular-nums">{fmtCHF(syncData.averages.revenue)}</td>
                      <td className="py-2 text-right tabular-nums">{fmtCHF(syncData.averages.expenses)}</td>
                      <td className="py-2 text-right tabular-nums">{fmtCHF(syncData.averages.ebit)}</td>
                      <td className="py-2 text-right tabular-nums">{syncData.averages.ebitMargin}%</td>
                      <td className="py-2 text-right">—</td>
                    </tr>
                  </tbody>
                </table>
                <div className="text-[11px] text-gray-500 mt-3 flex items-center gap-2">
                  <span>💡 Klicken Sie auf ▶ um Belege eines Jahres zu sehen, einzeln auszuschliessen oder manuelle Posten zu ergänzen.</span>
                </div>
              </div>
            )}

            <h3 className="text-xs uppercase tracking-wider text-[#C4A55A] mb-3">Anpassungen für FCF-Basis</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
              <NumInput label="Ø Jahresumsatz (CHF)" value={financeAdj.avgRevenue}
                onChange={(v) => setFinanceAdj({ ...financeAdj, avgRevenue: v })} testId="adj-revenue" />
              <NumInput label="Ø Jahresaufwand (CHF)" value={financeAdj.avgExpenses}
                onChange={(v) => setFinanceAdj({ ...financeAdj, avgExpenses: v })} testId="adj-expenses" />
              <NumInput label="EBIT-Marge Anpassung (%)" value={financeAdj.ebitMarginAdj} step="0.5"
                onChange={(v) => setFinanceAdj({ ...financeAdj, ebitMarginAdj: v })}
                hint="Bereinigt Einmaleffekte. Bsp: +5 für unterschätztes EBIT."
                testId="adj-margin" />
              <NumInput label="Effektiver Steuersatz (%)" value={financeAdj.taxRate} step="0.1"
                onChange={(v) => setFinanceAdj({ ...financeAdj, taxRate: v })} testId="adj-tax" />
              <NumInput label="CapEx / Investitionen (CHF)" value={financeAdj.capex}
                onChange={(v) => setFinanceAdj({ ...financeAdj, capex: v })}
                hint="Erhaltungs-Investitionen pro Jahr" testId="adj-capex" />
            </div>

            <div className="bg-gradient-to-r from-[#C4A55A]/10 to-transparent border border-[#C4A55A]/30 rounded-xl p-4 mb-5">
              <div className="text-xs uppercase tracking-wider text-[#C4A55A] mb-2">Berechnete FCF-Basis</div>
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div>
                  <div className="text-gray-400 text-xs">EBIT</div>
                  <div className="text-lg tabular-nums">CHF {fmtCHF(computedFcf.ebit)}</div>
                </div>
                <div>
                  <div className="text-gray-400 text-xs">NOPAT</div>
                  <div className="text-lg tabular-nums">CHF {fmtCHF(computedFcf.nopat)}</div>
                </div>
                <div>
                  <div className="text-gray-400 text-xs">Free Cash Flow</div>
                  <div className="text-xl font-bold text-[#C4A55A] tabular-nums" data-testid="computed-fcf">CHF {fmtCHF(computedFcf.fcf)}</div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setStep(1)}
                className="px-4 py-2 text-gray-400 hover:text-white text-sm"
              >Zurück</button>
              <button
                onClick={() => setStep(3)}
                disabled={!computedFcf.fcf}
                data-testid="goto-step3"
                className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-gradient-to-r from-[#C4A55A] to-[#B8974E] text-black font-semibold disabled:opacity-40"
              >
                Weiter zur DCF-Bewertung <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </section>
        )}

        {/* Step 3: DCF Parameters + Result */}
        {step === 3 && (
          <>
            <section className="bg-[#0f0f0f] border border-white/10 rounded-2xl p-6" data-testid="step3-content">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="h-5 w-5 text-[#C4A55A]" />
                <h2 className="text-lg font-semibold">DCF-Bewertungsparameter</h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                <NumInput label="Wachstum Phase 1 (J 1-5, %)" value={dcfParams.growth1} step="0.5"
                  onChange={(v) => setDcfParams({ ...dcfParams, growth1: v })} testId="dcf-g1" />
                <NumInput label="Wachstum Phase 2 (J 6-10, %)" value={dcfParams.growth2} step="0.5"
                  onChange={(v) => setDcfParams({ ...dcfParams, growth2: v })} testId="dcf-g2" />
                <NumInput label="WACC (%)" value={dcfParams.wacc} step="0.5"
                  onChange={(v) => setDcfParams({ ...dcfParams, wacc: v })} testId="dcf-wacc" />
                <NumInput label="Ewiges Wachstum (%)" value={dcfParams.terminalGrowth} step="0.1"
                  onChange={(v) => setDcfParams({ ...dcfParams, terminalGrowth: v })} testId="dcf-tg" />
                <NumInput label="Nettoverschuldung (CHF)" value={dcfParams.netDebt}
                  onChange={(v) => setDcfParams({ ...dcfParams, netDebt: v })}
                  hint="Bankschulden − liquide Mittel" testId="dcf-debt" />
                <NumInput label="Anzahl Aktien" value={dcfParams.shares}
                  onChange={(v) => setDcfParams({ ...dcfParams, shares: v })} testId="dcf-shares" />
                <NumInput label="Sicherheitsmarge (%)" value={dcfParams.marginOfSafety} step="1"
                  onChange={(v) => setDcfParams({ ...dcfParams, marginOfSafety: v })} testId="dcf-mos" />
                <NumInput label="FCF-Basis (CHF)" value={Math.round(computedFcf.fcf)} disabled
                  testId="dcf-fcf-display" />
              </div>
              <label className="block mb-4">
                <span className="text-xs text-gray-400">Notiz (optional)</span>
                <input type="text" value={dcfParams.note}
                  onChange={(e) => setDcfParams({ ...dcfParams, note: e.target.value })}
                  className="mt-1 w-full bg-black/60 border border-white/15 rounded-lg px-3 py-2 text-white text-sm"
                  data-testid="dcf-note"
                  placeholder="z.B. Konservatives Szenario, Q1 2026" />
              </label>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setStep(2)}
                  className="px-4 py-2 text-gray-400 hover:text-white text-sm"
                >Zurück</button>
                <button
                  onClick={calculate}
                  disabled={calculating}
                  data-testid="dcf-calculate"
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-gradient-to-r from-[#C4A55A] to-[#B8974E] text-black font-semibold disabled:opacity-50"
                >
                  {calculating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calculator className="h-4 w-4" />}
                  Bewertung berechnen
                </button>
              </div>
            </section>

            {result && <ResultPanel result={result.result} createdAt={result.createdAt} note={result.note} />}
          </>
        )}

        {/* History */}
        {history.length > 0 && (
          <section className="bg-[#0f0f0f] border border-white/10 rounded-2xl p-6" data-testid="history-section">
            <div className="flex items-center gap-2 mb-4">
              <History className="h-5 w-5 text-gray-400" />
              <h2 className="text-lg font-semibold">Bewertungs-Historie</h2>
              <span className="text-xs text-gray-500 ml-2">{history.length} Einträge</span>
            </div>
            <div className="space-y-2">
              {history.slice(0, 10).map((h, i) => (
                <div key={i} className="flex items-center justify-between bg-black/40 rounded-lg p-3 text-sm border border-white/5"
                     data-testid={`history-row-${i}`}>
                  <div>
                    <div className="text-gray-300">{new Date(h.createdAt).toLocaleString('de-CH')}</div>
                    {h.note && <div className="text-xs text-gray-500">{h.note}</div>}
                  </div>
                  <div className="text-right">
                    <div className="text-[#C4A55A] font-bold tabular-nums">CHF {fmtCHF(h.result?.valuePerShare)}/Aktie</div>
                    <div className="text-xs text-gray-500">EV CHF {fmtCHF(h.result?.enterpriseValue)}</div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

const NumInput = ({ label, value, onChange, step = '1', hint, testId, disabled }) => (
  <label className="block">
    <span className="text-xs text-gray-400">{label}</span>
    <input
      type="number"
      step={step}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange && onChange(parseFloat(e.target.value))}
      className="mt-1 w-full bg-black/60 border border-white/15 rounded-lg px-3 py-2 text-white tabular-nums focus:border-[#C4A55A] focus:outline-none disabled:opacity-60"
      data-testid={testId}
    />
    {hint && <span className="text-[10px] text-gray-500 mt-0.5 inline-block">{hint}</span>}
  </label>
);

const DocumentDetail = ({ year, onToggleExclude, onAddManual, onRemoveManual, newManual, setNewManual, saving }) => {
  const docs = year.documents || [];
  const manual = year.manualAdjustments || [];
  const invoices = docs.filter((d) => d.kind === 'invoice');
  const receipts = docs.filter((d) => d.kind === 'receipt');

  return (
    <div className="space-y-3 text-xs">
      {!docs.length && (
        <p className="text-gray-500 italic">Keine Belege aus AbaNinja für dieses Jahr.</p>
      )}

      {invoices.length > 0 && (
        <details open className="bg-black/40 rounded p-2">
          <summary className="cursor-pointer text-emerald-300 font-medium">
            Erlöse / Rechnungen ({invoices.length})
          </summary>
          <DocTable docs={invoices} onToggleExclude={onToggleExclude} testIdPrefix={`inv-${year.year}`} />
        </details>
      )}

      {receipts.length > 0 && (
        <details open className="bg-black/40 rounded p-2">
          <summary className="cursor-pointer text-red-300 font-medium">
            Aufwand / Belege ({receipts.length})
          </summary>
          <DocTable docs={receipts} onToggleExclude={onToggleExclude} testIdPrefix={`rcp-${year.year}`} />
        </details>
      )}

      <div className="bg-[#C4A55A]/5 border border-[#C4A55A]/30 rounded p-3">
        <div className="text-[#C4A55A] font-medium mb-2">Manuelle Anpassungen</div>
        {manual.length > 0 && (
          <ul className="space-y-1 mb-2">
            {manual.map((m, i) => (
              <li key={i} className="flex items-center justify-between bg-black/30 rounded px-2 py-1"
                  data-testid={`manual-${year.year}-${i}`}>
                <span className={m.kind === 'revenue' ? 'text-emerald-300' : 'text-red-300'}>
                  {m.kind === 'revenue' ? '+ Umsatz' : '− Aufwand'}: {m.label}
                </span>
                <span className="flex items-center gap-2">
                  <span className="tabular-nums">CHF {fmtCHF(m.amount)}</span>
                  <button onClick={() => onRemoveManual(i)} className="text-gray-500 hover:text-red-400">×</button>
                </span>
              </li>
            ))}
          </ul>
        )}
        <div className="grid grid-cols-12 gap-2 items-end">
          <label className="col-span-5 block">
            <span className="text-[10px] text-gray-500">Bezeichnung</span>
            <input
              type="text"
              value={newManual.label}
              onChange={(e) => setNewManual({ ...newManual, label: e.target.value })}
              placeholder="z.B. Verkauf Liegenschaft Genf"
              className="mt-0.5 w-full bg-black/60 border border-white/15 rounded px-2 py-1 text-white text-xs"
              data-testid={`manual-label-${year.year}`}
            />
          </label>
          <label className="col-span-3 block">
            <span className="text-[10px] text-gray-500">Typ</span>
            <select
              value={newManual.kind}
              onChange={(e) => setNewManual({ ...newManual, kind: e.target.value })}
              className="mt-0.5 w-full bg-black/60 border border-white/15 rounded px-2 py-1 text-white text-xs"
              data-testid={`manual-kind-${year.year}`}
            >
              <option value="revenue">Umsatz</option>
              <option value="expense">Aufwand</option>
            </select>
          </label>
          <label className="col-span-2 block">
            <span className="text-[10px] text-gray-500">CHF</span>
            <input
              type="number"
              step="100"
              value={newManual.amount}
              onChange={(e) => setNewManual({ ...newManual, amount: e.target.value })}
              className="mt-0.5 w-full bg-black/60 border border-white/15 rounded px-2 py-1 text-white text-xs tabular-nums"
              data-testid={`manual-amount-${year.year}`}
            />
          </label>
          <button
            onClick={onAddManual}
            disabled={saving}
            data-testid={`manual-add-${year.year}`}
            className="col-span-2 px-2 py-1 rounded bg-[#C4A55A] text-black text-xs font-semibold disabled:opacity-50"
          >Hinzufügen</button>
        </div>
      </div>
    </div>
  );
};

const DocTable = ({ docs, onToggleExclude, testIdPrefix }) => (
  <table className="w-full mt-1">
    <thead className="text-[10px] text-gray-500">
      <tr>
        <th className="text-left py-1 px-1">An / Aus</th>
        <th className="text-left py-1 px-1">Datum</th>
        <th className="text-left py-1 px-1">Nr.</th>
        <th className="text-left py-1 px-1">Beschreibung</th>
        <th className="text-left py-1 px-1">Kontakt</th>
        <th className="text-right py-1 px-1">Betrag</th>
      </tr>
    </thead>
    <tbody>
      {docs.map((d) => (
        <tr key={d.id} className={`border-t border-white/5 ${d.excluded ? 'opacity-40 line-through' : ''}`}
            data-testid={`${testIdPrefix}-row-${d.id}`}>
          <td className="py-1 px-1">
            <input
              type="checkbox"
              checked={!d.excluded}
              onChange={() => onToggleExclude(d.id, d.excluded)}
              className="accent-[#C4A55A]"
              data-testid={`${testIdPrefix}-toggle-${d.id}`}
            />
          </td>
          <td className="py-1 px-1 text-gray-400 tabular-nums">{(d.date || '').slice(0, 10)}</td>
          <td className="py-1 px-1 text-gray-500">{d.number || '—'}</td>
          <td className="py-1 px-1 text-gray-300 max-w-xs truncate" title={d.title}>{d.title || '—'}</td>
          <td className="py-1 px-1 text-gray-400 max-w-xs truncate">{d.contact || '—'}</td>
          <td className="py-1 px-1 text-right tabular-nums">CHF {fmtCHF(d.amount)}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const ResultPanel = ({ result, createdAt, note }) => (
  <section className="bg-gradient-to-br from-[#1a1a1a] via-[#141414] to-black border border-[#C4A55A]/40 rounded-2xl p-6"
           data-testid="dcf-result">
    <div className="flex items-center justify-between mb-5">
      <div className="flex items-center gap-2">
        <CheckCircle2 className="h-6 w-6 text-emerald-400" />
        <h2 className="text-xl font-bold">Bewertungs-Ergebnis</h2>
      </div>
      <div className="text-xs text-gray-500">
        {new Date(createdAt).toLocaleString('de-CH')}
      </div>
    </div>
    {note && <p className="text-sm text-gray-400 mb-4 italic">„{note}"</p>}

    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      <ResultCard label="Enterprise Value" value={`CHF ${fmtCHF(result.enterpriseValue)}`} />
      <ResultCard label="Equity Value" value={`CHF ${fmtCHF(result.equityValue)}`} />
      <ResultCard label="Wert / Aktie" value={`CHF ${fmtCHF(result.valuePerShare)}`} accent gold />
      <ResultCard label="Mit Sicherheitsmarge" value={`CHF ${fmtCHF(result.purchasePriceWithMoS)}`} accent />
    </div>

    <div className="bg-black/40 rounded-xl p-4 mb-5">
      <div className="text-xs uppercase tracking-wider text-gray-400 mb-3">Cashflow-Plan (10 Jahre)</div>
      <table className="w-full text-xs">
        <thead className="text-gray-500">
          <tr>
            <th className="text-left py-1">Jahr</th>
            <th className="text-right py-1">Wachstum</th>
            <th className="text-right py-1">FCF (CHF)</th>
            <th className="text-right py-1">Diskontfaktor</th>
            <th className="text-right py-1">Barwert (CHF)</th>
          </tr>
        </thead>
        <tbody>
          {result.projections.map((p) => (
            <tr key={p.year} className="border-t border-white/5">
              <td className="py-1">{p.year}</td>
              <td className="py-1 text-right">{p.growth}%</td>
              <td className="py-1 text-right tabular-nums">{fmtCHF(p.fcf)}</td>
              <td className="py-1 text-right tabular-nums">{p.discountFactor.toFixed(4)}</td>
              <td className="py-1 text-right tabular-nums text-[#C4A55A]">{fmtCHF(p.presentValue)}</td>
            </tr>
          ))}
          <tr className="border-t border-[#C4A55A]/30 text-[#C4A55A]">
            <td colSpan={4} className="py-1 font-semibold">Summe Barwerte (10 Jahre)</td>
            <td className="py-1 text-right font-bold tabular-nums">{fmtCHF(result.sumPVOperating)}</td>
          </tr>
          <tr>
            <td colSpan={4} className="py-1 text-gray-400">Terminal Value (Gordon)</td>
            <td className="py-1 text-right tabular-nums">{fmtCHF(result.terminalValue)}</td>
          </tr>
          <tr>
            <td colSpan={4} className="py-1 text-gray-400">Barwert Terminal Value</td>
            <td className="py-1 text-right tabular-nums text-[#C4A55A]">{fmtCHF(result.pvTerminal)}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-xs text-amber-200 flex gap-2">
      <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
      <span>
        Diese Bewertung ist eine <strong>indikative interne Schätzung</strong> und wird nicht
        an Investoren kommuniziert. Für die offizielle Ausgabepreis-Festlegung dient sie als Referenz.
      </span>
    </div>
  </section>
);

const ResultCard = ({ label, value, accent, gold }) => (
  <div className={`rounded-xl p-4 ${
    gold ? 'bg-[#C4A55A]/15 border border-[#C4A55A]/40' :
    accent ? 'bg-emerald-500/10 border border-emerald-500/30' :
    'bg-black/40 border border-white/5'
  }`}>
    <div className="text-xs uppercase tracking-wider text-gray-400 mb-1">{label}</div>
    <div className={`text-xl font-bold tabular-nums ${gold ? 'text-[#C4A55A]' : accent ? 'text-emerald-300' : 'text-white'}`}>
      {value}
    </div>
  </div>
);
